import { StateKey } from '../../utils/flipState'
import { RelationProductList } from 'common/types/entities/list'
import { RelationProductType, Sort } from 'common/constants'
import { fetchRelationProductList } from 'common/services/relationProduct'
import { DEFAULT_PAGE_SIZE, RelationProductParam } from 'common/types/params/list'
import { createCacheParam, isCacheHit, ReduxCachedState } from 'common/utils/reduxCache'
import { CommonState } from 'common/types/commonState'
import { fetchZzimedRelationProducts, setRelationProductZzim } from 'common/services/zzim'
import { Error } from 'common/types/entities/error'
import HttpStatus from 'common/utils/httpStatus'

export interface StoreRelationProductState extends ReduxCachedState {
  vmdList: RelationProductList
}

const initialRelationProductList: RelationProductList = {
  hasMore: false,
  relationProducts: null,
}

const initialState: StoreRelationProductState = {
  vmdList: null,
  cacheParam: null,
}

const FETCH_VMD = '/store/relationProduct/FETCH_VMD'
const SET_VMD_ZZIM = '/product/relationProduct/SET_VMD_ZZIM'

export const fetchVmdList = (curState: StateKey, storeId: string) => async (dispatch, getState: () => CommonState) => {
  const queryParams: RelationProductParam = {
    storeId,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    relationProductType: RelationProductType.VMD,
    sort: Sort.RECENT,
  }

  const cacheParam = createCacheParam(FETCH_VMD, 'fetchStoreVmdList', queryParams)
  if (isCacheHit(getState().storeRelationProduct[curState], cacheParam)) {
    return
  }

  const vmdList: RelationProductList = await fetchRelationProductList(queryParams)
  if (vmdList && vmdList.relationProducts) {
    const { relationProducts } = vmdList
    const ids = relationProducts && relationProducts.map(relationProduct => relationProduct._id)
    if (ids) {
      const zzimProducts = await fetchZzimedRelationProducts(ids)
      relationProducts.forEach(relationProduct => {
        const zzim = zzimProducts[relationProduct._id]
        relationProduct.zzimCount = zzim && zzim.count
        relationProduct.isMyZzim = (zzim && zzim.zzim) || false
      })
    }
  }

  dispatch({
    curState,
    cacheParam,
    type: FETCH_VMD,
    vmdList: vmdList || initialRelationProductList,
  })
}

export const toggleRelationProductZzim = (
  curState: StateKey,
  relationProductId: string,
  zzimed: boolean,
) => async dispatch => {
  const responseData = await setRelationProductZzim(relationProductId, zzimed)
  if (!responseData.bSuccess) {
    const error: Error = {
      status: responseData.sErrorCode === 'ACCESS_DENIED' ? HttpStatus.UNAUTHORIZED : HttpStatus.OK,
      message: responseData.htReturnValue.sMessage,
    }
    throw error
  }

  dispatch({
    curState,
    relationProductId,
    type: SET_VMD_ZZIM,
  })
}

const updateZzim = (relationProductList: RelationProductList, id: string) => {
  const { relationProducts } = relationProductList

  const index = relationProducts.findIndex(relationProduct => relationProduct._id === id)
  const updatedProduct = {
    ...relationProducts[index],
    isMyZzim: !relationProducts[index].isMyZzim,
  }

  return {
    ...relationProductList,
    relationProducts: [...relationProducts.slice(0, index), updatedProduct, ...relationProducts.slice(index + 1)],
  }
}

// reducers
const storeRelationProductReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_VMD:
      const { vmdList, cacheParam } = action
      return {
        vmdList,
        cacheParam,
      }
    case SET_VMD_ZZIM:
      return {
        ...state,
        vmdList: updateZzim(state.vmdList, action.relationProductId),
      }
    default:
      return state
  }
}

export default storeRelationProductReducer
